import { useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { InputLabelHeight } from "@covertree/types";
import { Button, Form, FormTextField } from "@covertree/ui";
import { Modal } from "@covertree/ui";

import {
  RenewalStatus,
  RenewalWithCheckReceivalFragment,
} from "@covertree/agent-portal-graphql";
import { MarkRenewalAsPaid, markRenewalAsPaidValidator } from "./validation";
import { useMarkRenewalAsPaid } from "./useMarkRenewalAsPaid";
import { InformationBox } from "@covertree/ui/content";

interface MarkRenewalAdPaidModalProps {
  open: boolean;
  onClose: () => void;
  renewal: RenewalWithCheckReceivalFragment;
}

export function MarkRenewalAsPaidModal({
  open,
  onClose,
  renewal,
}: MarkRenewalAdPaidModalProps) {
  const { handleSubmit: submit, loading } = useMarkRenewalAsPaid(
    renewal,
    onClose
  );

  const defaultValues = useMemo(
    () => ({
      description: renewal?.checkReceival?.description,
    }),
    [renewal?.checkReceival?.description]
  );

  const form = useForm<MarkRenewalAsPaid>({
    mode: "all",
    defaultValues,
    resolver: yupResolver(markRenewalAsPaidValidator),
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { isValid },
  } = form;

  const isPaymentFailedRenewal = useMemo(
    () => renewal?.status === RenewalStatus.PaymentFailed,
    [renewal]
  );

  const isRenewalMarkedAsPaid = useMemo(
    () => !!renewal?.checkReceival,
    [renewal]
  );

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        isRenewalMarkedAsPaid
          ? "Edit invoice settlement description"
          : "Mark Mortgage Bill Renewal as Paid"
      }
      disableClose={loading}
      maxWidth="md">
      <Form onSubmit={handleSubmit(submit)}>
        {isPaymentFailedRenewal && (
          <InformationBox mb={2} sx={{ color: "text.primary" }}>
            <Typography variant="subtitle2" fontWeight={400}>
              Performing this operation will trigger payment retrial for this
              renewal.
            </Typography>
          </InformationBox>
        )}
        <FormProvider {...form}>
          <FormTextField
            multiline
            minRows={4}
            maxRows={4}
            control={control}
            name="description"
            id="mark-as-paid-description"
            label=""
            placeholder="Please provide an invoice settlement description..."
            labelHeight={InputLabelHeight.SMALL}
            hiddenLabel
            disabled={loading}
            InputProps={{
              sx: (theme) => ({
                "& > textarea": {
                  cursor: "auto",
                  paddingTop: 1,
                },
                "& > textarea::-webkit-scrollbar": {
                  width: theme.spacing(1.5),
                },
                "& > textarea::-webkit-scrollbar-thumb": {
                  borderRadius: theme.spacing(1),
                  backgroundColor: theme.colors.scrollbar,
                },
              }),
            }}
          />
          <Box mt={4} columnGap={3} display="flex">
            <Button
              loading={loading}
              disabled={loading || !isValid}
              type="submit">
              Confirm
            </Button>
            <Button disabled={loading} onClick={handleClose} variant="outlined">
              Cancel
            </Button>
          </Box>
        </FormProvider>
      </Form>
    </Modal>
  );
}
